import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tile } from '../../../../shared/components/ui/primitives';
import { getInventoryMonthlyRegister } from '../../../services/api/implementation/impl';
import { useProjectWarehouses, validWarehouseId } from '../../../hooks/useInventorySelection';
import {
  formatINR,
  formatMonthKey,
  formatQty,
  formatRateINR,
  movementTypeLabel
} from '../../../utils/inventoryValue';
import { inrLakhCrore } from '../../../utils/inrLakhCrore';

const lakhHint = (value) => (Math.abs(Number(value)) >= 100000 ? inrLakhCrore(value) : undefined);

const signedINR = (value) => {
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return '—';
  if (n === 0) return '₹0';
  return `${n > 0 ? '+' : ''}${formatINR(n)}`;
};

const Th = ({ children, right, className = '' }) => (
  <th className={`px-3 py-2 ${right ? 'text-right' : ''} ${className}`}>{children}</th>
);
const Num = ({ children, className = '' }) => (
  <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${className}`}>{children}</td>
);

/** Qty over value, for the item table. */
const QtyValue = ({ qty, value }) => (
  <Num>
    <span className="block text-gray-900">{formatQty(qty)}</span>
    <span className="block text-xs text-gray-500">{formatINR(value)}</span>
  </Num>
);

/**
 * W4.3: the month's stock register. Opening, received, issued, adjusted and
 * closing stock, as quantity and value, per item; with per-warehouse totals
 * and what each kind of stock change added up to.
 */
const StockRegister = ({ projectId, month }) => {
  const [pickedWarehouse, setPickedWarehouse] = useState('');
  const [search, setSearch] = useState('');
  const { active: activeWarehouses, hasMany } = useProjectWarehouses(projectId);
  const warehouseId = validWarehouseId(pickedWarehouse, activeWarehouses);

  useEffect(() => {
    setPickedWarehouse('');
  }, [projectId]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['inventoryMonthlyRegister', projectId, month, warehouseId],
    queryFn: () =>
      getInventoryMonthlyRegister({ projectId, month, warehouseId: warehouseId || undefined }),
    enabled: !!projectId && !!month
  });

  const warehouses = useMemo(() => data?.warehouses || [], [data?.warehouses]);
  const totals = data?.totals || {};
  const warehouseName = useMemo(
    () => new Map(warehouses.map((w) => [Number(w.warehouse_id), w.name])),
    [warehouses]
  );

  // One list of change types across the warehouses shown.
  const byType = useMemo(() => {
    const map = new Map();
    warehouses.forEach((w) =>
      (w.by_type || []).forEach((t) => {
        const row = map.get(t.type) || { type: t.type, value: 0, movements: 0, priced: false };
        if (t.value != null) {
          row.value += Number(t.value) || 0;
          row.priced = true;
        }
        row.movements += Number(t.movements) || 0;
        map.set(t.type, row);
      })
    );
    return [...map.values()].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  }, [warehouses]);

  const items = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = data?.items || [];
    if (!term) return rows;
    return rows.filter((i) =>
      `${i.item_name || ''} ${i.category_name || ''}`.toLowerCase().includes(term)
    );
  }, [data?.items, search]);
  const showWarehouseColumn = warehouses.length > 1;
  const unpricedClosing = (data?.items || []).filter(
    (i) => Number(i.closing_qty) > 0 && i.closing_rate == null
  ).length;

  const loaded = !!data;
  const value = (key) => (loaded ? formatINR(totals[key]) : '—');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#1E4A53]">
            Stock register · {formatMonthKey(data?.month || month)}
          </h2>
          <p className="text-sm text-gray-500">
            Opening + in − out ± adjustments = closing, for every item, at the rates the stock
            moved at.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {hasMany && (
            <label className="flex flex-col text-xs text-gray-500">
              Warehouse
              <select
                value={warehouseId || ''}
                onChange={(e) => setPickedWarehouse(e.target.value ? Number(e.target.value) : '')}
                className="h-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800">
                <option value="">All warehouses</option>
                {activeWarehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {isError && (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.response?.data?.message || error?.message || 'Could not load the stock register'}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Tile label="Opening value" value={value('opening_value')} hint={lakhHint(totals.opening_value)} />
        <Tile label="In" value={value('in_value')} hint="Received, returned, transferred in" />
        <Tile label="Out" value={value('out_value')} hint="Released, transferred or lent out" />
        <Tile label="Adjustments" value={loaded ? signedINR(totals.adjust_value) : '—'} hint="Audit, edits, rate fixes" />
        <Tile
          label="Closing value"
          tone="brand"
          value={value('closing_value')}
          hint={lakhHint(totals.closing_value) || `${formatQty(totals.items)} items`}
        />
      </div>
      {unpricedClosing > 0 && (
        <p className="-mt-3 text-xs text-gray-500">
          {unpricedClosing} item{unpricedClosing === 1 ? '' : 's'} closed the month with stock but
          no rate, counted at ₹0.
        </p>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {showWarehouseColumn && (
          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            <p className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-700">
              By warehouse
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                  <tr>
                    <Th>Warehouse</Th>
                    <Th right>Opening</Th>
                    <Th right>In</Th>
                    <Th right>Out</Th>
                    <Th right>Adjust</Th>
                    <Th right>Closing</Th>
                  </tr>
                </thead>
                <tbody>
                  {warehouses.map((w) => (
                    <tr key={w.warehouse_id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{w.name || '—'}</td>
                      <Num>{formatINR(w.totals?.opening_value)}</Num>
                      <Num>{formatINR(w.totals?.in_value)}</Num>
                      <Num>{formatINR(w.totals?.out_value)}</Num>
                      <Num>{signedINR(w.totals?.adjust_value)}</Num>
                      <Num className="font-semibold text-gray-900">
                        {formatINR(w.totals?.closing_value)}
                      </Num>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <p className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-700">
            What changed the stock
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                <tr>
                  <Th>Change</Th>
                  <Th right>Entries</Th>
                  <Th right>Value</Th>
                </tr>
              </thead>
              <tbody>
                {byType.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-gray-400">
                      {isFetching ? 'Loading…' : 'No stock changes this month'}
                    </td>
                  </tr>
                ) : (
                  byType.map((t) => (
                    <tr key={t.type} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{movementTypeLabel(t.type)}</td>
                      <Num>{t.movements.toLocaleString('en-IN')}</Num>
                      <Num
                        className={
                          t.value > 0 ? 'text-green-700' : t.value < 0 ? 'text-red-600' : ''
                        }>
                        {t.priced ? signedINR(t.value) : '—'}
                      </Num>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <p className="text-sm font-semibold text-gray-700">
            Items{loaded ? ` (${(data?.items || []).length})` : ''}
          </p>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item or category"
            className="h-9 w-full max-w-xs rounded-lg border border-gray-200 px-3 text-sm"
          />
        </div>
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-[#F4F7F8] text-xs uppercase text-gray-500">
              <tr>
                <Th>Item</Th>
                {showWarehouseColumn && <Th>Warehouse</Th>}
                <Th right>Opening</Th>
                <Th right>In</Th>
                <Th right>Out</Th>
                <Th right>Adjust</Th>
                <Th right>Closing</Th>
                <Th right>Rate</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={showWarehouseColumn ? 8 : 7}
                    className="px-3 py-8 text-center text-gray-400">
                    {isFetching ? 'Loading…' : search ? 'No item matches' : 'No stock this month'}
                  </td>
                </tr>
              ) : (
                items.map((i) => (
                  <tr
                    key={`${i.inventory_id}-${i.warehouse_id}`}
                    className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <span className="block text-gray-900">{i.item_name || '—'}</span>
                      <span className="block text-xs text-gray-500">
                        {[i.category_name, i.unit].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </td>
                    {showWarehouseColumn && (
                      <td className="px-3 py-2 text-gray-700">
                        {warehouseName.get(Number(i.warehouse_id)) || '—'}
                      </td>
                    )}
                    <QtyValue qty={i.opening_qty} value={i.opening_value} />
                    <QtyValue qty={i.in_qty} value={i.in_value} />
                    <QtyValue qty={i.out_qty} value={i.out_value} />
                    <Num className="text-gray-700">{signedINR(i.adjust_value)}</Num>
                    <Num>
                      <span className="block font-semibold text-gray-900">
                        {formatQty(i.closing_qty)}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {formatINR(i.closing_value)}
                      </span>
                    </Num>
                    <Num className="text-gray-700">
                      {i.closing_rate == null ? (
                        <span
                          className="text-xs text-gray-400"
                          title={Number(i.closing_qty) > 0 ? 'No rate yet, counted at ₹0' : undefined}>
                          {Number(i.closing_qty) > 0 ? 'No rate' : '—'}
                        </span>
                      ) : (
                        formatRateINR(i.closing_rate)
                      )}
                    </Num>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockRegister;
